import React, { useContext, type FC } from 'react';

import { useSiteMetadata, useProductUrls, useExternalLogos, withImage, useSmartlingStrings } from '@lucid/hooks';
import { isBrowser, withURL } from '@lucid/utils';
import { getColor } from '@lucid/theme';
import { Form, Field } from 'react-final-form';

import reactHTMLParser from '../../../utils/reactHTMLParser';
import { isValidEnterpriseEmail, composeValidators, isValidEmail } from '../../marketo-form/validation/validation';
import Input from '../../form-fields/input/input';
import Link from '../../link/link';
import Box from '../../box/box';
import Text from '../../text/text';
import Image from '../../image/image';
import LayoutContext from '../../context/layout-context';
import type { Lucid } from '../../../types/types';

export type SSOService = 'Google' | 'Slack' | 'Microsoft';

interface SSOButtonData {
  service: SSOService;
  /** The identifier to route to the respective SSO service */
  paramsKey: 'googleFederatedLogin' | 'microsoftLogin' | 'slackLogin';
  /** The url for the logo image */
  logo: Lucid.Directus.Generated.External_Logo;
}

export interface CtaWithSSOProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Determines certain style changes for dark backgrounds */
  hasDarkBackground?: boolean;
  /** Optional use of an email input field */
  hasEmailInput?: boolean;
  /** Determines the exact color of the SSO text */
  ssoSeparatorColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** link text to override default ("Sign up free") */
  linkText?: string;
  /** Link url to override default (pricing page) */
  linkUrl?: string;
  /** Array of additional custom params */
  customURLParams?: Lucid.URL.CustomParams[];
  /** Allows to select which SSO services to use */
  ssoServices?: SSOService[];
}

const CtaWithSSO: FC<CtaWithSSOProps> = ({
  className,
  hasDarkBackground = false,
  hasEmailInput = false,
  ssoSeparatorColor,
  linkUrl,
  linkText,
  customURLParams,
  ssoServices = ['Google', 'Slack', 'Microsoft'],
  ...rest
}) => {
  const { urlLangPrefix, shortCode } = useContext(LayoutContext);
  const { pricingUrl } = useProductUrls(urlLangPrefix);
  const href = isBrowser() ? window.location.href : '';
  const { product } = useSiteMetadata();
  const productKey = product === 'spark' ? 'spark-' : '';
  const customMediaQuery = `@media only screen and (min-width: ${'400px'})`;

  const [google, slack, microsoft] = useExternalLogos(['google-icon', 'slack-icon', 'microsoft-icon']);
  const initialSsoServiceLinks: SSOButtonData[] = [
    {
      service: 'Google',
      paramsKey: 'googleFederatedLogin',
      logo: google,
    },
    {
      service: 'Microsoft',
      paramsKey: 'microsoftLogin',
      logo: microsoft,
    },
    {
      service: 'Slack',
      paramsKey: 'slackLogin',
      logo: slack,
    },
  ];

  const ssoServiceLinks = initialSsoServiceLinks.filter(({ service }) => ssoServices.includes(service));

  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_sso_or_continue_with',
      'mktg_sso_sign_in_product',
      'mktg_sso_sign_in',
      'mktg_sso_terms',
      'mktg_generics_sign_up_free',
    ],
    shortCode,
  });

  const convertURL = (urlPath: string) => {
    if (['fr', 'de'].includes(shortCode)) return `https://lucid.co${urlLangPrefix}${urlPath}`;

    return `https://lucid.co${urlPath}`;
  };

  // TODO: [Growth Team Test] - remove customURLParams prop when complete
  const upperLink = withURL(linkUrl || pricingUrl, {
    customParams: customURLParams,
    product,
    urlLangPrefix,
  });

  return (
    <Box
      {...rest}
      css={{
        label: 'ctaWithSSO',
        width: 'min(370px, 100%)',
        maxWidth: '460px',
        [customMediaQuery]: {
          minWidth: '370px',
          width: 'fit-content',
        },
      }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      {/* 
        TODO: hasEmailInput may be used for future AB tests.
        Keeping for now, but re-evaluate if we don't end up using it
      */}
      {hasEmailInput && (
        <Form validateOnBlur initialValues={null} onSubmit={() => undefined}>
          {({ values, hasValidationErrors }) => (
            <form
              action={`${pricingUrl}?referer=${encodeURIComponent(href)}&quickRegister=true&fromMarketing=true`}
              method="POST"
            >
              <Field
                isFullWidth
                component={Input}
                css={{ textAlign: 'left' }}
                id="registrationEmailOpt"
                label="Work Email"
                name="registrationEmailOpt"
                placeholder="email@company.com"
                type="email"
                validate={
                  !values.registrationEmailOpt
                    ? () => undefined
                    : composeValidators(isValidEmail('email'), isValidEnterpriseEmail('email', true))
                }
                value={values.registrationEmailOpt}
              />
              <Link
                asButton
                aria-disabled={hasValidationErrors}
                aria-label="Go to Lucidchart pricing page"
                css={{
                  width: '100%',
                  minWidth: 'unset',
                  margin: 0,
                }}
                type="submit"
              >
                {linkText || withSmartling('mktg_generics_sign_up_free')}
              </Link>
            </form>
          )}
        </Form>
      )}
      {!hasEmailInput && (
        <Link
          displayAsButton
          aria-describedby="heroLinkDescription"
          css={{
            width: '100%',
            minWidth: 'unset',
            margin: 0,
          }}
          href={upperLink}
          variant={hasDarkBackground ? 'secondary' : 'primary'}
        >
          {linkText || withSmartling('mktg_generics_sign_up_free')}
        </Link>
      )}
      {!!ssoServiceLinks.length && (
        <>
          <Text
            css={theme => ({
              textAlign: 'center',
              padding: '0 12px',
              color: ssoSeparatorColor
                ? getColor({
                    color: ssoSeparatorColor,
                    theme,
                  })
                : hasDarkBackground
                  ? theme.colors.coolGrey[20]
                  : theme.colors.coolGrey[70],
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              margin: '20px 0',
              '&:before, &:after': {
                content: '""',
                position: 'absolute',
                top: '51%',
                borderBottom: `1px solid ${
                  getColor({
                    color: ssoSeparatorColor,
                    theme,
                  }) || theme.colors.coolGrey[20]
                }`,
                width: '50%',
              },
              '&:after': {
                marginLeft: '12px',
              },
              '&:before': {
                marginLeft: 'calc(-50% - 12px)',
              },
            })}
          >
            {withSmartling('mktg_sso_or_continue_with')}
          </Text>
          <Box
            css={{
              display: 'inline-flex',
              gap: '8px',
              flexDirection: 'column',
              minWidth: '100%',
              [customMediaQuery]: {
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
              },
            }}
          >
            {ssoServiceLinks.map(({ service, paramsKey, logo }) => (
              <Link
                key={paramsKey}
                displayAsButton
                aria-label={withSmartling('mktg_sso_sign_in_product', { service })}
                css={theme => [
                  {
                    margin: 0,
                    padding: `12px ${theme.space.s16}px`,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '12px',
                    background: 'white',
                    color: theme.colors.coolGrey[100],
                    textWrap: 'nowrap',
                    minWidth: '100%',
                    [customMediaQuery]: {
                      flex: `1 1 calc((100% / ${ssoServiceLinks.length}) - ${(ssoServiceLinks.length - 1) * 8}px)`,
                      minWidth: 'fit-content',
                      maxWidth: '50%',
                    },
                  },
                  hasEmailInput && {
                    [customMediaQuery]: {
                      flex: 'auto',
                      maxWidth: 'max-content',
                    },
                  },
                ]}
                // TODO: [Growth Team Test] - remove customURLParams prop when complete
                href={
                  customURLParams
                    ? `https://lucid.app/users/${paramsKey}?level_id=${productKey}free&return_url=/users/login?${customURLParams
                        .map(params =>
                          Object.keys(params)
                            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
                            .join('&'),
                        )
                        .join('&')}`
                    : `https://lucid.app/users/${paramsKey}?level_id=${productKey}free&return_url=/users/login`
                }
              >
                <Image css={{ height: '19px' }} imageAlt="" imageSrc={withImage(logo.image)} />
                {!hasEmailInput && (
                  <>
                    <span css={{ [customMediaQuery]: { display: 'none' } }}>
                      {withSmartling('mktg_sso_sign_in_product', { service })}
                    </span>
                    <span
                      css={{
                        display: 'none',
                        [customMediaQuery]: { display: 'inline' },
                      }}
                    >
                      {withSmartling('mktg_sso_sign_in')}
                    </span>
                  </>
                )}
              </Link>
            ))}
          </Box>
          <Text
            css={theme => [
              theme.shared.paragraphNestedLinkStyles(theme),
              {
                maxWidth: '400px',
                textAlign: 'left',
                marginTop: theme.space.s16,
                a: {
                  color: theme.colors.blue[90],
                },
              },
              hasDarkBackground && {
                color: 'white',
                a: {
                  color: theme.colors.blue[20],
                },
              },
              hasEmailInput && {
                textAlign: 'center',
              },
            ]}
            variant="labelXS400"
          >
            {reactHTMLParser(
              withSmartling('mktg_sso_terms', {}, [
                `<a href="${convertURL('/tos')}" target="blank" rel="noopener noreferrer">{}</a>`,
                `<a href="${convertURL('/privacy')}" target="_blank" rel="noopener noreferrer">{}</a>`,
              ]),
            )}
          </Text>
        </>
      )}
    </Box>
  );
};

export { CtaWithSSO };
export default CtaWithSSO;
